import { SUPPLIER_BACKEND_API_ENDPOINTS } from "../config/apiConfig";
import { apiClient } from "./api-client.service";
import type { ApiResponse } from "./api-client.service";

import type {
  Role,
  RolesFilter,
  RolesResponse,
  RoleFilterPayload,
} from "../types/rbac-roles.types";

const endpoints = SUPPLIER_BACKEND_API_ENDPOINTS.rbacRoles;

const failure = (error: unknown, fallback: string) => ({
  success: false,
  statusCode: 500,
  message: error instanceof Error ? error.message : fallback,
  resData: null,
});

export const rbacRolesService = {
  async list(filter: RolesFilter): Promise<RolesResponse> {
    try {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      });

      return await apiClient<RolesResponse>(endpoints.list(params.toString()), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(error, "Failed to load role list.") as unknown as RolesResponse;
    }
  },

  async details(id: number | string): Promise<ApiResponse<Role>> {
    try {
      return await apiClient<ApiResponse<Role>>(endpoints.details(id), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(error, "Failed to load role details.") as unknown as ApiResponse<Role>;
    }
  },

  async create(payload: RoleFilterPayload): Promise<ApiResponse<Role>> {
    try {
      return await apiClient<ApiResponse<Role>>(endpoints.create, {
        method: "POST",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(error, "Failed to create role.") as unknown as ApiResponse<Role>;
    }
  },

  async update(id: number | string, payload: Partial<RoleFilterPayload>): Promise<ApiResponse<Role>> {
    try {
      return await apiClient<ApiResponse<Role>>(endpoints.update(id), {
        method: "PATCH",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(error, "Failed to update role.") as unknown as ApiResponse<Role>;
    }
  },

  async delete(id: number | string): Promise<ApiResponse<unknown>> {
    try {
      return await apiClient<ApiResponse<unknown>>(endpoints.delete(id), {
        method: "DELETE",
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(error, "Failed to delete role.") as unknown as ApiResponse<unknown>;
    }
  },
};
